type EventCallback<T = any> = (data: T) => void;

interface EventBusEvents {
  [key: string]: any;
}

class EventBus<Events extends EventBusEvents = EventBusEvents> {
  private events: Map<keyof Events, Set<EventCallback>> = new Map();

  /**
   * Subscribe to an event
   * @param event - The event name to listen to
   * @param callback - The callback function to execute when event is emitted
   * @returns Unsubscribe function
   */
  on<K extends keyof Events>(event: K, callback: EventCallback<Events[K]>): () => void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }

    const callbacks = this.events.get(event)!;
    callbacks.add(callback);

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Unsubscribe from an event
   * @param event - The event name to stop listening to
   * @param callback - The specific callback to remove
   */
  off<K extends keyof Events>(event: K, callback: EventCallback<Events[K]>): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.events.delete(event);
      }
    }
  }

  /**
   * Emit an event to all listeners
   * @param event - The event name to emit
   * @param data - The data to pass to event listeners
   */
  emit<K extends keyof Events>(event: K, data: Events[K]): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event handler for "${String(event)}":`, error);
        }
      });
    }
  }

  /**
   * Subscribe to an event but automatically unsubscribe after first emission
   * @param event - The event name to listen to once
   * @param callback - The callback function to execute once
   * @returns Unsubscribe function
   */
  once<K extends keyof Events>(event: K, callback: EventCallback<Events[K]>): () => void {
    const unsubscribe = this.on(event, (data) => {
      unsubscribe();
      callback(data);
    });
    return unsubscribe;
  }

  /**
   * Remove all listeners for a specific event or all events
   * @param event - Optional specific event to clear, if not provided clears all events
   */
  removeAllListeners<K extends keyof Events>(event?: K): void {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
  }

  /**
   * Get list of events that have listeners
   * @returns Array of event names that have active listeners
   */
  getEvents(): Array<keyof Events> {
    return Array.from(this.events.keys());
  }

  /**
   * Get number of listeners for a specific event
   * @param event - The event name to check
   * @returns Number of active listeners for the event
   */
  listenerCount<K extends keyof Events>(event: K): number {
    const callbacks = this.events.get(event);
    return callbacks ? callbacks.size : 0;
  }
}

// Create a singleton instance for global use
const eventBus = new EventBus();

export default eventBus;
export { EventBus };
export type { EventCallback, EventBusEvents };
